import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Trophy, Sparkles, Clock, Users, ArrowLeft, Upload, Check, LogIn, Image as ImageIcon, Heart, Share2,
} from "lucide-react";
import { toast } from "sonner";
import ShareEntryDialog from "@/components/ShareEntryDialog";

const MAX_DESC = 300;

function formatDateFr(s) {
  if (!s) return "";
  try {
    return new Date(s).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return s;
  }
}

function isDeadlinePassed(endDate) {
  if (!endDate) return false;
  try {
    const end = new Date(endDate);
    // inclusive: deadline considered passed the day AFTER end_date
    end.setHours(23, 59, 59, 999);
    return new Date() > end;
  } catch {
    return false;
  }
}

export default function ConcoursDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [concours, setConcours] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  // The current user's own entry, if any (used for the Share button)
  const myEntry = user ? entries.find((e) => e.user_id === user.user_id) : null;

  // Role-aware back link
  const backTo = user?.role === "talent" ? "/talent/concours" : (user?.role === "creator" || user?.role === "professional") ? "/createurs/concours" : "/";

  const load = async () => {
    try {
      const [c, e] = await Promise.all([
        api.get(`/concours/${id}`),
        api.get(`/concours/${id}/entries`),
      ]);
      setConcours(c.data);
      setEntries(Array.isArray(e.data) ? e.data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Concours introuvable");
      navigate(user?.role === "talent" ? "/talent/concours" : "/");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading) {
    return (
      <AppShell>
        <div className="flex items-center justify-center py-32">
          <div className="w-10 h-10 border-2 border-[#C68E59] border-t-transparent rounded-full animate-spin" />
        </div>
      </AppShell>
    );
  }
  if (!concours) return null;

  const deadlinePassed = isDeadlinePassed(concours.end_date);
  const isActive = concours.status === "active" && !deadlinePassed;
  const canParticipateRole = user?.role === "talent";

  return (
    <AppShell>
      <SEO
        title={`${concours.title} — Concours`}
        description={(concours.description || concours.theme || "Concours Helencia").toString().slice(0, 200)}
        image={typeof window !== "undefined" ? `${window.location.origin}/api/og/concours/${concours.id}.png` : undefined}
        url={typeof window !== "undefined" ? `${window.location.origin}/concours/${concours.id}` : undefined}
        type="article"
      />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-16">
        {/* Back link */}
        <Link
          to={backTo}
          className="inline-flex items-center gap-2 text-sm text-[#6E4B2A] hover:text-[#1A1A1A] mb-6"
          data-testid="concours-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Tous les concours
        </Link>

        {/* Hero */}
        <div className="rounded-3xl overflow-hidden bg-white border border-[#EADDC9] shadow-[0_20px_60px_-30px_rgba(110,75,42,0.25)]">
          {concours.image_url && (
            <div className="aspect-[21/9] md:aspect-[24/9] overflow-hidden bg-[#FAF3EA]">
              <img
                src={concours.image_url}
                alt={concours.title}
                className="w-full h-full object-cover"
                data-testid="concours-detail-cover"
              />
            </div>
          )}

          <div className="p-6 md:p-10">
            <div className="flex flex-wrap items-center gap-3">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-[#C68E59]">
                <Trophy className="w-3.5 h-3.5" /> {concours.theme || "Concours"}
              </span>
              {isActive ? (
                <span className="text-[10px] font-semibold uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-[#E7F5EC] text-[#2E7D32] border border-[#BFE4C5]">
                  En cours
                </span>
              ) : (
                <span className="text-[10px] font-semibold uppercase tracking-wider px-2.5 py-0.5 rounded-full bg-[#FAF3EA] text-[#8B8680] border border-[#E5E5E5]">
                  Fermé
                </span>
              )}
            </div>

            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl text-[#1A1A1A] mt-3 leading-tight" data-testid="concours-detail-title">
              {concours.title}
            </h1>

            {concours.description && (
              <p className="text-[#4A4A4A] leading-relaxed mt-5 max-w-3xl whitespace-pre-line">
                {concours.description}
              </p>
            )}

            {/* Meta row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8 pt-8 border-t border-[#EADDC9]">
              <Meta icon={Sparkles} label="Récompense" value={concours.prize || "—"} />
              <Meta icon={Clock} label="Date limite" value={formatDateFr(concours.end_date)} />
              <Meta icon={Users} label="Participants" value={`${concours.participants_count ?? 0}`} testid="concours-participants-count" />
              <Meta icon={Trophy} label="Statut" value={isActive ? "Ouvert" : "Clôturé"} />
            </div>

            {/* Rules */}
            {concours.rules && (
              <div className="mt-8 rounded-2xl bg-[#FFFBF5] border border-[#EADDC9] p-6">
                <p className="caption-label text-[#A26F3E]">Règles de participation</p>
                <p className="text-sm text-[#4A4A4A] leading-relaxed mt-3 whitespace-pre-line">{concours.rules}</p>
              </div>
            )}

            {/* CTA */}
            <div className="mt-8">
              {!user ? (
                <Link to="/login" className="inline-flex" data-testid="concours-login-cta">
                  <Button className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white px-6 h-11">
                    <LogIn className="w-4 h-4 mr-2" /> Connectez-vous pour participer
                  </Button>
                </Link>
              ) : !canParticipateRole ? (
                <div className="text-sm text-[#8B8680] italic">
                  Seuls les talents peuvent participer aux concours.
                </div>
              ) : concours.has_participated ? (
                <div className="flex flex-wrap items-center gap-3" data-testid="concours-already-participated">
                  <div className="inline-flex items-center gap-2 text-sm text-[#2E7D32] bg-[#E7F5EC] border border-[#BFE4C5] px-4 py-2.5 rounded-full">
                    <Check className="w-4 h-4" /> Votre participation a bien été envoyée
                  </div>
                  {myEntry && (
                    <Button
                      onClick={() => setShareOpen(true)}
                      className="rounded-full bg-[#1A1A1A] hover:bg-[#000] text-white px-5 h-10"
                      data-testid="concours-share-my-entry"
                    >
                      <Share2 className="w-4 h-4 mr-2" /> Partager ma participation
                    </Button>
                  )}
                </div>
              ) : !isActive ? (
                <Button disabled className="rounded-full bg-[#C4C4C4] text-white px-6 h-11 cursor-not-allowed">
                  Participations clôturées
                </Button>
              ) : (
                <Button
                  onClick={() => setSubmitOpen(true)}
                  className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white px-7 h-11"
                  data-testid="concours-participate-btn"
                >
                  <Upload className="w-4 h-4 mr-2" /> Participer
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Podium (top 3) — visible when concours clôturé */}
        {!isActive && entries.length > 0 && <Podium entries={entries.slice(0, 3)} contestId={id} />}

        {/* Live ranking — visible pendant le concours */}
        {isActive && entries.length > 0 && <LiveRanking entries={entries.slice(0, 5)} currentUser={user} />}

        {/* Gallery */}
        <div className="mt-16">
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <p className="caption-label text-[#C68E59]">Galerie des participations</p>
              <h2 className="font-serif text-2xl sm:text-3xl text-[#1A1A1A] mt-1">
                {entries.length > 0 ? `${entries.length} soumission${entries.length > 1 ? "s" : ""}` : "Soyez le premier à participer"}
              </h2>
            </div>
          </div>

          {entries.length === 0 ? (
            <div className="rounded-3xl bg-white border border-dashed border-[#EADDC9] p-12 text-center">
              <ImageIcon className="w-10 h-10 text-[#C68E59] mx-auto opacity-60" />
              <p className="text-sm text-[#8B8680] mt-4">Aucune participation pour le moment.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="concours-entries-grid">
              {entries.map((e) => (
                <EntryCard
                  key={e.id}
                  entry={e}
                  contestId={id}
                  currentUser={user}
                  canVote={isActive}
                  onUpdate={(patch) => setEntries((prev) => prev.map((x) => x.id === e.id ? { ...x, ...patch } : x))}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Submit Modal */}
      <SubmitDialog
        open={submitOpen}
        onClose={() => setSubmitOpen(false)}
        contestId={id}
        onSuccess={() => {
          setSubmitOpen(false);
          toast.success("Votre participation a bien été envoyée !");
          load();
        }}
      />

      {/* Share Modal (only when user has own entry) */}
      <ShareEntryDialog
        open={shareOpen && !!myEntry}
        onClose={() => setShareOpen(false)}
        entryId={myEntry?.id}
        contestId={id}
      />
    </AppShell>
  );
}

/* ============================ SUB-COMPONENTS ============================ */

function Meta({ icon: Icon, label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-[#A26F3E]">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className="text-[#1A1A1A] font-medium mt-1.5 text-sm">{value}</div>
    </div>
  );
}

/* ============================ PODIUM (concours clôturé) ============================ */
function LiveRanking({ entries, currentUser }) {
  const max = Math.max(1, ...entries.map((e) => e.votes_count ?? 0));
  return (
    <div className="mt-10 rounded-3xl bg-[#1A1A1A] text-white p-6 sm:p-8 relative overflow-hidden" data-testid="live-ranking">
      <div className="absolute -top-24 -right-16 w-72 h-72 rounded-full bg-[#C68E59]/25 blur-[100px] pointer-events-none" />
      <div className="relative flex items-baseline justify-between gap-4 mb-5">
        <div>
          <p className="caption-label text-[#E6C29A] inline-flex items-center gap-2">
            <span className="w-1.5 h-1.5 rounded-full bg-[#2E7D32] animate-pulse" /> Classement en direct
          </p>
          <h2 className="font-serif text-2xl sm:text-3xl mt-1">Top {entries.length} <span className="italic text-[#E6C29A]">du moment</span></h2>
        </div>
        <p className="text-xs text-white/60 hidden sm:block">Mis à jour à chaque vote</p>
      </div>
      <ol className="relative space-y-2.5">
        {entries.map((e, i) => {
          const votes = e.votes_count ?? 0;
          const mine = currentUser && e.user_id === currentUser.user_id;
          return (
            <li key={e.id} className={`flex items-center gap-3 rounded-2xl px-3 py-2.5 ${mine ? "bg-[#C68E59]/20 border border-[#C68E59]/40" : "bg-white/5"}`} data-testid={`ranking-row-${i + 1}`}>
              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${i === 0 ? "bg-[#E6C29A] text-[#1A1A1A]" : "bg-white/10 text-white/80"}`}>{i + 1}</span>
              {e.user?.picture ? (
                <img src={e.user.picture} alt="" className="w-9 h-9 rounded-full object-cover shrink-0" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center text-xs font-semibold shrink-0">{(e.user?.name || "?").slice(0, 1).toUpperCase()}</div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{e.user?.name || "Talent"}{mine && <span className="text-[#E6C29A] text-xs ml-2">· vous</span>}</p>
                <div className="h-1 rounded-full bg-white/10 mt-1.5 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#C68E59] to-[#E6C29A] rounded-full transition-all duration-700" style={{ width: `${Math.max(4, (votes / max) * 100)}%` }} />
                </div>
              </div>
              <span className="inline-flex items-center gap-1 text-xs font-semibold shrink-0"><Heart className="w-3 h-3 text-[#C68E59] fill-[#C68E59]" /> {votes}</span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function Podium({ entries, contestId }) {
  // Ensure 1, 2, 3 visual ordering: silver, gold, bronze (gold center)
  const [first, second, third] = entries;
  const slots = [
    { rank: 2, entry: second, height: "h-44 sm:h-52", color: "from-[#E6C29A] to-[#D4A574]", text: "Argent" },
    { rank: 1, entry: first,  height: "h-56 sm:h-72", color: "from-[#D4A574] to-[#A26F3E]", text: "Or" },
    { rank: 3, entry: third,  height: "h-36 sm:h-44", color: "from-[#C09573] to-[#8B5E34]", text: "Bronze" },
  ].filter((s) => s.entry);

  return (
    <div className="mt-12 mb-8 rounded-3xl bg-white border border-[#EADDC9] p-6 sm:p-10 shadow-[0_20px_60px_-30px_rgba(110,75,42,0.25)]" data-testid="podium-section">
      <div className="text-center mb-8">
        <p className="caption-label text-[#C68E59]">Résultats du concours</p>
        <h2 className="font-serif text-3xl sm:text-4xl text-[#1A1A1A] mt-1">
          Le <span className="italic text-[#A26F3E]">podium</span>
        </h2>
        <p className="text-xs text-[#6E4B2A] mt-2">Classement final selon les votes de la communauté Helencia.</p>
      </div>

      <div className="grid grid-cols-3 gap-3 sm:gap-6 items-end max-w-3xl mx-auto">
        {slots.map(({ rank, entry, height, color, text }) => (
          <PodiumSlot
            key={entry.id}
            rank={rank}
            entry={entry}
            heightClass={height}
            gradient={color}
            label={text}
            contestId={contestId}
          />
        ))}
      </div>
    </div>
  );
}

function PodiumSlot({ rank, entry, heightClass, gradient, label, contestId }) {
  const u = entry.user || {};
  return (
    <Link
      to={`/contest/${contestId}/entry/${entry.id}`}
      className="group flex flex-col items-center"
      data-testid={`podium-slot-${rank}`}
    >
      {/* Photo with medal */}
      <div className="relative w-full aspect-[3/4] rounded-2xl overflow-hidden bg-[#FAF3EA] border border-[#EADDC9] shadow-[0_10px_30px_-15px_rgba(110,75,42,0.4)] group-hover:scale-[1.02] transition">
        <img src={entry.image_url} alt={u.name} className="w-full h-full object-cover" loading="lazy" />
        <div className={`absolute top-2 left-2 w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white shadow-md bg-gradient-to-br ${gradient}`}>
          {rank}
        </div>
        <div className="absolute top-2 right-2 inline-flex items-center gap-1 bg-white/95 backdrop-blur px-2 py-1 rounded-full text-xs font-semibold text-[#1A1A1A]">
          <Heart className="w-3 h-3 text-[#C68E59] fill-[#C68E59]" /> {entry.votes_count ?? 0}
        </div>
      </div>

      {/* Name + medal label */}
      <div className="mt-3 text-center w-full">
        <div className="text-[10px] uppercase tracking-wider text-[#A26F3E] font-semibold">
          {label}
        </div>
        <div className="text-sm font-medium text-[#1A1A1A] mt-0.5 truncate">
          {u.name || "Talent"}
        </div>
      </div>

      {/* Plinth */}
      <div className={`mt-3 w-full ${heightClass} rounded-t-2xl bg-gradient-to-b ${gradient} flex items-start justify-center pt-3 text-white font-serif text-2xl sm:text-3xl`}>
        {rank}
      </div>
    </Link>
  );
}

function EntryCard({ entry, contestId, currentUser, canVote, onUpdate }) {
  const u = entry.user || {};
  const [busy, setBusy] = useState(false);
  const isOwner = currentUser?.user_id === entry.user_id;
  const voteCount = entry.votes_count ?? 0;
  const hasVoted = !!entry.has_voted;

  const handleVote = async () => {
    if (!currentUser) {
      toast.info("Connectez-vous pour voter");
      return;
    }
    if (isOwner) return; // silent, UI disables
    if (!canVote) {
      toast.info("Les votes sont clôturés pour ce concours");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/concours/${contestId}/entries/${entry.id}/vote`);
      onUpdate({ has_voted: data.has_voted, votes_count: data.votes_count });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de voter");
    } finally {
      setBusy(false);
    }
  };

  const voteDisabled = busy || isOwner || !canVote;

  return (
    <div className="group rounded-2xl overflow-hidden bg-white border border-[#EADDC9]" data-testid={`contest-entry-${entry.id}`}>
      <div className="aspect-square overflow-hidden bg-[#FAF3EA] relative">
        <img
          src={entry.image_url}
          alt={u.name || "Participation"}
          className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
          loading="lazy"
        />
        <button
          type="button"
          onClick={handleVote}
          disabled={voteDisabled}
          title={isOwner ? "Vous ne pouvez pas voter pour votre propre participation" : (!canVote ? "Votes clôturés" : (hasVoted ? "Retirer mon vote" : "Voter"))}
          className={`absolute top-2 right-2 inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-xs font-semibold shadow-sm transition
            ${hasVoted
              ? "bg-[#C68E59] text-white"
              : "bg-white/95 text-[#6E4B2A] hover:bg-white"}
            ${voteDisabled && !hasVoted ? "opacity-70 cursor-not-allowed" : ""}
          `}
          data-testid={`entry-vote-${entry.id}`}
        >
          <Heart className={`w-3.5 h-3.5 ${hasVoted ? "fill-current" : ""}`} />
          <span data-testid={`entry-vote-count-${entry.id}`}>{voteCount}</span>
        </button>
      </div>
      <div className="p-3">
        <div className="flex items-center gap-2">
          {u.picture ? (
            <img src={u.picture} alt="" className="w-7 h-7 rounded-full object-cover" />
          ) : (
            <div className="w-7 h-7 rounded-full bg-[#FAF3EA] flex items-center justify-center text-[10px] font-semibold text-[#A26F3E]">
              {(u.name || "?").slice(0, 1).toUpperCase()}
            </div>
          )}
          <span className="text-xs text-[#1A1A1A] font-medium truncate">{u.name || "Talent"}</span>
        </div>
        {entry.description && (
          <p className="text-[11px] text-[#6E4B2A] mt-2 line-clamp-2 leading-snug">{entry.description}</p>
        )}
      </div>
    </div>
  );
}

function SubmitDialog({ open, onClose, contestId, onSuccess }) {
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setFile(null);
    if (preview) URL.revokeObjectURL(preview);
    setPreview(null);
    setDescription("");
  };

  useEffect(() => {
    if (!open) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Fichier image requis"); return; }
    if (f.size > 15 * 1024 * 1024) { toast.error("Image trop volumineuse (max 15 Mo)"); return; }
    if (preview) URL.revokeObjectURL(preview);
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const submit = async () => {
    if (!file) { toast.error("Sélectionnez une image"); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("description", description.slice(0, MAX_DESC));
      await api.post(`/concours/${contestId}/entries`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible d'envoyer la participation");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="concours-submit-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Participer au concours</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* File upload */}
          {preview ? (
            <div className="relative">
              <div className="aspect-square max-h-80 overflow-hidden rounded-2xl bg-[#FAF3EA] border border-[#EADDC9]">
                <img src={preview} alt="Aperçu" className="w-full h-full object-cover" data-testid="concours-submit-preview" />
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                className="mt-3 rounded-full border-[#EADDC9]"
                disabled={submitting}
              >
                <Upload className="w-3.5 h-3.5 mr-1" /> Choisir une autre image
              </Button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={submitting}
              className="w-full rounded-2xl border-2 border-dashed border-[#EADDC9] bg-[#FFFBF5] hover:bg-[#FAF3EA] p-10 text-center transition"
              data-testid="concours-submit-file-trigger"
            >
              <Upload className="w-8 h-8 text-[#C68E59] mx-auto mb-3" />
              <p className="font-serif text-lg text-[#1A1A1A]">Choisir une image</p>
              <p className="text-xs text-[#6E4B2A] mt-1">JPG / PNG — max 15 Mo — une seule photo</p>
            </button>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFile}
            className="hidden"
            data-testid="concours-submit-file-input"
          />

          {/* Description */}
          <div>
            <label className="text-xs uppercase tracking-wider text-[#A26F3E]">Description (optionnelle)</label>
            <Textarea
              placeholder="Quelques mots sur votre proposition…"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value.slice(0, MAX_DESC))}
              className="rounded-xl mt-1.5"
              data-testid="concours-submit-description"
            />
            <p className="text-[11px] text-[#8B8680] text-right mt-1">{description.length}/{MAX_DESC}</p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting} className="rounded-full">
            Annuler
          </Button>
          <Button
            onClick={submit}
            disabled={!file || submitting}
            className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white"
            data-testid="concours-submit-btn"
          >
            {submitting ? "Envoi…" : "Envoyer ma participation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
